/**
 * Panel - a floating glass surface over a surface's canvas.
 *
 * Not a column. Each panel is a `GlassCard` docked to one edge, inset from the
 * viewport so it reads as floating, with the conversation visible through and
 * around it. Resizing one changes only itself; nothing tiles and nothing gets
 * pushed.
 *
 * TWO GESTURES LIVE HERE AND MUST STAY INDEPENDENT:
 *
 *   • RESIZE - the handle on the panel's inner edge (the one facing the
 *     canvas). It is a SIBLING of the header, explicitly `draggable={false}`,
 *     and sits above the drag rails. Adding reorder once let the rails cover
 *     this edge and killed resizing entirely; the separation below is what
 *     stops that happening again.
 *   • REORDER - the header IS the drag handle, so no row is wasted on a grip.
 *     Controls inside the header stop propagation, so pressing an action never
 *     starts a drag.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiXLight } from 'react-icons/pi';
import GlassCard from '../brand/GlassCard';
import Skeleton from '../TailwindComponents/Skeleton';
import { PaneBodyProvider } from './PaneBody';
import { DockSide, PanelBounds } from './panelLayout';

interface Props {
  id: string;
  /** This panel's own resize limits. */
  bounds: PanelBounds;
  width: number;
  dock: DockSide;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  /** Extra header controls, left of the close button. */
  actions?: React.ReactNode;
  onClose?: () => void;
  onResize?: (width: number) => void;
  /** Raise above dock siblings when the user touches this panel. */
  onFocus?: () => void;
  zIndex?: number;
  /** Drag-to-redock. Disabled on narrow viewports. */
  draggable?: boolean;
  onDragStart?: () => void;
  /** Pointer position during a drag, for the surface's hit-test. */
  onDragMove?: (x: number, y: number) => void;
  /** Pointer position at release - where the panel should land. */
  onDragEnd?: (x?: number, y?: number) => void;
  /** Rendered as a full-height sheet instead of a float. */
  sheet?: boolean;
  /**
   * First-load state for this pane's CONTENT.
   *
   * Same bargain `Table` makes: the chrome - glass, header, close button —
   * stays put and only the body swaps for a pane-shaped skeleton, so a pane
   * that is still fetching never reads as a pane that failed to open.
   */
  isLoading?: boolean;
  children: React.ReactNode;
}

/** Wrap header controls so pressing one never begins a panel drag. */
export const PanelAction: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    draggable={false}
    onMouseDown={(e) => e.stopPropagation()}
    onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
    className="flex items-center gap-1"
  >
    {children}
  </span>
);

const Panel: React.FC<Props> = ({
  id,
  bounds,
  width,
  dock,
  title,
  icon,
  actions,
  onClose,
  onResize,
  onFocus,
  zIndex = 30,
  draggable = false,
  onDragStart,
  onDragMove,
  onDragEnd,
  sheet = false,
  isLoading = false,
  children,
}) => {
  const { t } = useTranslation();
  const draggingRef = useRef(false);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  // The resize edge faces the canvas: a left-docked panel resizes from its
  // right edge, a right-docked one from its left.
  const handleSide: 'left' | 'right' = dock === 'left' ? 'right' : 'left';

  useEffect(() => {
    if (!onResize || sheet) return;

    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current || !nodeRef.current) return;
      const rect = nodeRef.current.getBoundingClientRect();
      const next = dock === 'left' ? e.clientX - rect.left : rect.right - e.clientX;
      onResize(next);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsResizing(false);
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dock, sheet, onResize]);

  const startResize = useCallback((e: React.MouseEvent) => {
    // A resize must never bubble into the header's drag.
    e.stopPropagation();
    draggingRef.current = true;
    setIsResizing(true);
    document.body.style.userSelect = 'none';
  }, []);

  const onHandleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!onResize) return;
      const step = e.shiftKey ? 48 : 16;
      if (e.key === 'ArrowLeft') { e.preventDefault(); onResize(width - step); }
      if (e.key === 'ArrowRight') { e.preventDefault(); onResize(width + step); }
    },
    [onResize, width]
  );

  /**
   * The header ROW is the drag handle, driven by POINTER events.
   *
   * HTML5 drag-and-drop was the previous approach and never completed a drop:
   * between absolutely-positioned docks the browser found no valid target, so
   * the panel could be picked up and never put down. Pointer events have no
   * drop-target model at all - the surface hit-tests on pointerup - which
   * removes the whole class of failure.
   *
   * A drag only begins after the pointer has actually MOVED past a threshold,
   * so a plain click on the header (or on a control inside it) is never
   * mistaken for a drag.
   */
  const DRAG_THRESHOLD = 4;
  const pressRef = useRef<{ x: number; y: number; dragging: boolean } | null>(null);

  const onHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    // Primary button only - but a pen or touch contact reports no button at
    // all, so only a POSITIVE button (right/middle) is rejected.
    if (!draggable || (typeof e.button === 'number' && e.button > 0)) return;
    pressRef.current = { x: e.clientX, y: e.clientY, dragging: false };
  }, [draggable]);

  const onHeaderPointerMove = useCallback((e: React.PointerEvent) => {
    const press = pressRef.current;
    if (!press || press.dragging) {
      if (press?.dragging) onDragMove?.(e.clientX, e.clientY);
      return;
    }
    if (Math.hypot(e.clientX - press.x, e.clientY - press.y) < DRAG_THRESHOLD) return;
    press.dragging = true;
    onDragStart?.();
    onDragMove?.(e.clientX, e.clientY);
  }, [onDragStart, onDragMove]);

  const onHeaderPointerUp = useCallback((e: React.PointerEvent) => {
    const press = pressRef.current;
    pressRef.current = null;
    if (press?.dragging) onDragEnd?.(e.clientX, e.clientY);
  }, [onDragEnd]);

  const headerProps = draggable
    ? {
        onPointerDown: onHeaderPointerDown,
        onPointerMove: onHeaderPointerMove,
        onPointerUp: onHeaderPointerUp,
        onPointerCancel: () => { pressRef.current = null; onDragEnd?.(); },
        'data-testid': `panel-header-${id}`,
        'data-draggable': 'true',
        className: 'cursor-grab active:cursor-grabbing select-none touch-none',
      }
    : { 'data-testid': `panel-header-${id}` };

  return (
    <div
      ref={nodeRef}
      data-testid={`panel-${id}`}
      data-panel={id}
      data-dock={dock}
      data-sheet={sheet}
      style={sheet ? { zIndex } : { width, zIndex }}
      onMouseDown={onFocus}
      className={clsx(
        'relative pointer-events-auto',
        sheet ? 'h-full w-[min(24rem,92vw)]' : 'flex-1 min-h-0'
      )}
    >
      <GlassCard
        padding="none"
        density="compact"
        icon={icon}
        headerProps={headerProps as React.HTMLAttributes<HTMLDivElement>}
        title={<span className="block truncate">{title}</span>}
        actions={
          <PanelAction>
            {actions}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                data-testid={`panel-close-${id}`}
                aria-label={t('Close panel')}
                className="p-1 rounded-md text-fg-muted hover:text-fg-strong
                           transition-colors duration-200 ease-slide"
              >
                <PiXLight className="w-4 h-4" />
              </button>
            )}
          </PanelAction>
        }
        className="h-full flex flex-col overflow-hidden"
        bodyClassName="flex-1 min-h-0 overflow-y-auto"
      >
        {/*
          Everything below here KNOWS it is in a pane - see `PaneBody`. The
          panel already draws the glass, the header and the close button, so a
          body that would otherwise draw its own card or header drops that
          chrome instead of stacking a second one inside the first.

          Note this wraps the CHILDREN only: the panel's own `GlassCard` above
          is the chrome, and must not read the flag it is setting.
        */}
        <PaneBodyProvider>
          {isLoading ? <Skeleton variant="pane" /> : children}
        </PaneBodyProvider>
      </GlassCard>

      {onResize && !sheet && (
        <div
          role="separator"
          tabIndex={0}
          aria-orientation="vertical"
          aria-valuenow={width}
          aria-valuemin={bounds?.min}
          aria-valuemax={bounds?.max}
          aria-label={t('Resize panel')}
          data-testid={`panel-resize-${id}`}
          // Explicitly not a drag source: without this the header's HTML5
          // drag can claim the gesture and the edge reorders instead.
          draggable={false}
          onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onMouseDown={startResize}
          onKeyDown={onHandleKeyDown}
          className={clsx(
            // z-40 keeps this above the drag rails, which is the specific
            // layering that broke resizing before.
            'absolute inset-y-2 w-1.5 rounded-full cursor-col-resize z-40',
            handleSide === 'right' ? '-end-0.5' : '-start-0.5',
            'hover:bg-champagne/50 focus:bg-champagne/50 focus:outline-none',
            isResizing && 'bg-champagne/70',
            'transition-colors duration-200 ease-slide'
          )}
        />
      )}
    </div>
  );
};

/**
 * A drop ZONE: the whole area a panel can be dropped into, not a 12px rail.
 *
 * Rails were too small to hit and, under HTML5 DnD, never fired at all. These
 * are generous targets covering the dock region, hit-tested by the surface on
 * pointerup, so "drag the container and drop it anywhere in that area" is
 * literally what happens.
 */
export const PanelDropZone: React.FC<{
  dock: DockSide;
  index: number;
  active: boolean;
  hovered: boolean;
  label?: string;
  /** Fill the column - for the single zone of an empty dock. */
  fill?: boolean;
}> = ({ dock, index, active, hovered, label, fill = false }) => {
  if (!active) return null;

  return (
    <div
      data-testid={`panel-dropzone-${dock}-${index}`}
      data-dock={dock}
      data-index={index}
      data-dropzone="true"
      data-hovered={hovered}
      aria-hidden
      className={clsx(
        // Big enough to aim at, and it grows while the pointer is over it.
        'rounded-lg border-2 border-dashed pointer-events-none',
        'flex items-center justify-center transition-all duration-200 ease-slide',
        fill ? 'flex-1 self-stretch' : 'flex-shrink-0',
        hovered
          ? 'min-h-[6rem] flex-1 border-champagne bg-champagne/10'
          : clsx('border-hairline bg-surface-sunken/30', !fill && 'min-h-[3rem]')
      )}
    >
      {label && (
        <span className={clsx(
          'text-[11px] font-medium transition-colors duration-200 ease-slide',
          hovered ? 'text-fg-strong' : 'text-fg-muted'
        )}>
          {label}
        </span>
      )}
    </div>
  );
};

export default Panel;
